import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useExtendedState from '../../hooks/useExtendedState';
import { memberPropertiesType, memberType as memberPropType } from '../../types';
import Privileges from '../../utils/Privileges';
import { Privileged } from '../../hocs/withPrivileges';
import PrivilegedTextField from '../fields/privileged/PrivilegedTextField';
import ReadonlyField from '../fields/ReadonlyField';
import './_styles_/MemberDetailTab.css';
import MemberAssignedUsersTable from './MemberAssignedUsersTable';
import useMultiValidator from '../../hooks/useMultiValidator';
import {
    BALANCING_GROUP_LENGTH,
    MEMBER_ID_LENGTH,
    MEMBER_EMAILS_MAX_LENGTH,
    MEMBER_NAME_MAX_LENGTH,
    MEMBER_PHONE_MAX_LENGTH,
    memberBalancingGroupValidator,
    memberEmailsValidator,
    memberIdValidator,
    memberNameValidator,
    memberPhoneValidator,
} from '../../validators/members';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    ALL_MEMBERS_QUERY_NAME,
    MEMBER_DETAIL,
    TOGGLE_ACTIVATE_MEMBER,
    TOGGLE_SUSPEND_MEMBER,
    UPDATE_MEMBER,
} from '../../graphql/members';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import SuspensionButton from '../buttons/SuspensionButton';
import ActivationButton from '../buttons/ActivationButton';
import PrivilegedSelectField from '../fields/privileged/PrivilegedSelectField';
import PrivilegedCheckboxField from '../fields/privileged/PrivilegedCheckboxField';
import MemberCashLimitsTable from './MemberCashLimitsTable';
import PrivilegedCheckboxList from '../fields/privileged/PrivilegedCheckboxList';
import ExpandedListTab from '../ExpandedListTab';
import {
    AUCTIONEER_AND_TECH_AUCTIONEER,
    getMemberType,
    MEMBER,
    TECHNICAL_AUCTIONEER,
} from '../../utils/MemberTypes';
import InfoPanel from '../InfoPanel';
import {
    MEMBER_BLOCKER_AUCTIONEER_IN_AUCTION,
    MEMBER_BLOCKER_MEMBER_HAS_BIDS,
    MEMBER_BLOCKER_USER_ASSIGNED,
    transformMemberBlockers,
} from '../blockers/MemberBlockers';
import useIsMounted from '../../hooks/useIsMounted';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

MemberDetailTab.propTypes = {
    member: memberPropType.isRequired,
    memberProps: memberPropertiesType.isRequired,
};

function MemberDetailTab(props) { // NOSONAR javascript:S3776
    const {
        member,
        memberProps,
    } = props;
    const { t } = useTranslation();
    const {
        availableMemberTypes,
        availableZones,
        availableProductTypes,
        availablePaymentCurrencies,
        clearingEnabled,
        euAuctioneersEnabled,
    } = memberProps;
    const isMounted = useIsMounted();
    const isOnlyEnergyProductAvailable = availableProductTypes.includes('ENERGY') && availableProductTypes.length === 1;
    const activeState = useExtendedState(member.active);
    const suspendedState = useExtendedState(member.suspended);
    const memberIdState = useExtendedState(member.memberId);
    const nameState = useExtendedState(member.name);
    const memberTypeState = useExtendedState(getMemberType(member.memberType));
    const balancingGroupState = useExtendedState(member.balancingGroup);
    const emailsState = useExtendedState(member.emails);
    const phoneState = useExtendedState(member.phone);
    const paymentCurrencyCodeState = useExtendedState(member.paymentCurrency ? member.paymentCurrency.code : null);
    const foreignCurrencyAllowedState = useExtendedState(member.foreignCurrencyAllowed);
    const euAuctioneerState = useExtendedState(member.euAuctioneer);
    const zonesState = useExtendedState(member.zones.map((zone) => zone.code));
    const availableZonesMap = availableZones.map((availableZone) => ({ id: availableZone.code, label: availableZone.name }));
    const setNewMemberType = (newMemberType) => {
        paymentCurrencyCodeState.reset();
        foreignCurrencyAllowedState.reset();
        euAuctioneerState.reset();
        memberTypeState.setValue(getMemberType(newMemberType));
    };
    const { validators, isValid } = useMultiValidator(true, {
        memberId: compositeValidator(memberIdValidator(t), wafValidator(t)),
        name: compositeValidator(memberNameValidator(t), wafValidator(t)),
        balancingGroup: compositeValidator(memberBalancingGroupValidator(t, isOnlyEnergyProductAvailable), wafValidator(t)),
        emails: compositeValidator(memberEmailsValidator(t), wafValidator(t)),
        phone: memberPhoneValidator(t),
    }, {
        balancingGroup: !(isOnlyEnergyProductAvailable && !balancingGroupState.value),
    });
    const isChanged = memberIdState.isChanged || nameState.isChanged || memberTypeState.isChanged || balancingGroupState.isChanged || emailsState.isChanged || phoneState.isChanged || zonesState.isChanged || paymentCurrencyCodeState.isChanged || foreignCurrencyAllowedState.isChanged || euAuctioneerState.isChanged;
    const isChangedPromise = isChanged ? Promise.resolve() : Promise.reject();
    const [isDisabled, setIsDisabled] = useState(true);
    Promise.all([isChangedPromise, isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));
    let toggleSuspendMemberConfirmMessageKey = 'members.confirm-suspend-member';
    let toggleSuspendMemberFailureMessageKey = 'members.notification.member-suspend-failure';
    if (suspendedState.value) {
        toggleSuspendMemberConfirmMessageKey = 'members.confirm-unsuspend-member';
        toggleSuspendMemberFailureMessageKey = 'members.notification.member-unsuspend-failure';
    }
    const statesToResetOnSuccess = [memberIdState, nameState, memberTypeState, balancingGroupState, emailsState, phoneState, paymentCurrencyCodeState, foreignCurrencyAllowedState, euAuctioneerState, zonesState];
    const toggleSuspendMemberMutation = useExtendedMutation(TOGGLE_SUSPEND_MEMBER, {
        variables: { id: member.id },
        successCallback: () => {
            if (isMounted()) {
                suspendedState.changeInitialState(!suspendedState.value);
                suspendedState.reset();
            }
        },
        successMessageCreator: ({ toggleMemberSuspension: { suspended: updatedSuspended, name: updatedName, memberId: updatedMemberId } }) => {
            let toggleSuspendMemberSuccessMessageKey = 'members.notification.member-unsuspend-success';
            if (updatedSuspended) {
                toggleSuspendMemberSuccessMessageKey = 'members.notification.member-suspend-success';
            }
            return t(toggleSuspendMemberSuccessMessageKey, { name: updatedName, memberId: updatedMemberId });
        },
        failureMessageOptions: { key: toggleSuspendMemberFailureMessageKey, params: { name: member.name, memberId: member.memberId } },
        refetchQueries: [ALL_MEMBERS_QUERY_NAME, {
            query: MEMBER_DETAIL,
            variables: { id: member.id },
        }],
    });
    const isActivationButtonDisabled = activeState.value && member.deactivationBlockers?.length > 0;
    let toggleMemberActivationConfirmMessageKey = 'members.confirm-activate-member';
    let toggleMemberActivationFailureMessageKey = 'members.notification.member-activate-failure';
    if (activeState.value) {
        toggleMemberActivationConfirmMessageKey = 'members.confirm-deactivate-member';
        toggleMemberActivationFailureMessageKey = 'members.notification.member-deactivate-failure';
    }
    const toggleMemberActivationMutation = useExtendedMutation(TOGGLE_ACTIVATE_MEMBER, {
        variables: { id: member.id },
        successCallback: () => {
            if (isMounted()) {
                activeState.changeInitialState(!activeState.value);
                activeState.reset();
            }
        },
        successMessageCreator: ({ toggleMemberActivation: { active: updatedActive, name: updatedName, memberId: updatedMemberId } }) => {
            let toggleMemberActivationSuccessMessageKey = 'members.notification.member-deactivate-success';
            if (updatedActive) {
                toggleMemberActivationSuccessMessageKey = 'members.notification.member-activate-success';
            }
            return t(toggleMemberActivationSuccessMessageKey, { name: updatedName, memberId: updatedMemberId });
        },
        failureMessageOptions: { key: toggleMemberActivationFailureMessageKey, params: { name: member.name, memberId: member.memberId } },
        refetchQueries: [ALL_MEMBERS_QUERY_NAME, {
            query: MEMBER_DETAIL,
            variables: { id: member.id },
        }],
    });
    const updateMemberMutation = useExtendedMutation(UPDATE_MEMBER, {
        variables: {
            id: member.id,
            memberId: memberIdState.value,
            name: nameState.value,
            memberType: memberTypeState.value.serverType,
            balancingGroup: availableProductTypes.includes('ENERGY')
                ? balancingGroupState.value
                : null,
            emails: emailsState.value,
            phone: phoneState.value,
            paymentCurrency: clearingEnabled && memberTypeState.value.is(MEMBER)
                ? paymentCurrencyCodeState.value
                : null,
            foreignCurrencyAllowed: clearingEnabled && (memberTypeState.value.is(TECHNICAL_AUCTIONEER) || memberTypeState.value.is(AUCTIONEER_AND_TECH_AUCTIONEER))
                ? foreignCurrencyAllowedState.value
                : null,
            euAuctioneer: euAuctioneersEnabled && (memberTypeState.value.is(TECHNICAL_AUCTIONEER) || memberTypeState.value.is(AUCTIONEER_AND_TECH_AUCTIONEER))
                ? euAuctioneerState.value
                : null,
            zones: zonesState.value,
        },
        statesToResetOnSuccess,
        successMessageOptions: { key: 'members.notification.member-update-success', dataProperty: 'updateMember' },
        failureMessageOptions: { key: 'members.notification.member-update-failure', params: { name: member.name, memberId: member.memberId } },
        refetchQueries: [ALL_MEMBERS_QUERY_NAME, {
            query: MEMBER_DETAIL,
            variables: { id: member.id },
        }],
    });
    const sufficientPrivileges = [Privileges.MEMBER_EDIT];
    const canModifyRestrictedField = !member.modificationBlockers?.includes(MEMBER_BLOCKER_MEMBER_HAS_BIDS)
        && !member.modificationBlockers?.includes(MEMBER_BLOCKER_AUCTIONEER_IN_AUCTION);
    const canMemberTypeBeModified = canModifyRestrictedField
        && !member.modificationBlockers?.includes(MEMBER_BLOCKER_USER_ASSIGNED);
    return (
        <ExpandedListTab
            className="member-detail-tab"
            title={t('members.member-detail')}
        >
            <div className="member-detail-tab--readonly-fields">
                <ReadonlyField
                    label={t('members.suspended')}
                    value={suspendedState.value ? t('misc.yes') : t('misc.no')}
                    className="member-detail-tab--fields--suspended"
                />
                <ReadonlyField
                    label={t('members.active')}
                    value={activeState.value ? t('misc.yes') : t('misc.no')}
                    className="member-detail-tab--fields--active"
                />
            </div>
            <div className="member-detail-tab--fields">
                <PrivilegedTextField
                    sufficientPrivileges={canModifyRestrictedField ? sufficientPrivileges : []}
                    label={t('members.member-id')}
                    state={memberIdState}
                    className="member-detail-tab--fields--member-id"
                    inputId="member-detail-tab--fields--member-id"
                    validator={validators.memberId}
                    maxLength={MEMBER_ID_LENGTH}
                    placeholder={t('members.placeholder.member-id')}
                    isRequired
                />
                <PrivilegedTextField
                    sufficientPrivileges={sufficientPrivileges}
                    label={t('members.name')}
                    state={nameState}
                    validator={validators.name}
                    className="member-detail-tab--fields--name x-wide"
                    placeholder={t('members.placeholder.name')}
                    maxLength={MEMBER_NAME_MAX_LENGTH}
                    isRequired
                />
                {availableMemberTypes.length > 1 && (
                    <PrivilegedSelectField
                        sufficientPrivileges={canMemberTypeBeModified ? sufficientPrivileges : []}
                        inputId="member-detail-tab--fields--member-type"
                        label={t('members.member-type')}
                        state={memberTypeState}
                        value={memberTypeState.value.serverType}
                        translatedReadonlyValue={memberTypeState.value.translate(t)}
                        onChange={setNewMemberType}
                        className="member-detail-tab--fields--member-type"
                        options={availableMemberTypes.map((availableMemberType) => (
                            <option value={availableMemberType} key={availableMemberType}>{getMemberType(availableMemberType).translate(t)}</option>
                        ))}
                        isRequired
                    />
                )}
                {availableProductTypes.includes('ENERGY') && (
                    <PrivilegedTextField
                        sufficientPrivileges={canModifyRestrictedField ? sufficientPrivileges : []}
                        label={t('members.balancing-group')}
                        state={balancingGroupState}
                        validator={validators.balancingGroup}
                        className="member-detail-tab--fields--balancing-group wide"
                        maxLength={BALANCING_GROUP_LENGTH}
                        placeholder={t('members.placeholder.balancing-group')}
                        isRequired={isOnlyEnergyProductAvailable}
                    />
                )}
                <PrivilegedTextField
                    sufficientPrivileges={sufficientPrivileges}
                    label={t('members.emails')}
                    state={emailsState}
                    validator={validators.emails}
                    className="member-detail-tab--fields--emails x-wide"
                    inputMode="email"
                    placeholder={t('members.placeholder.emails')}
                    maxLength={MEMBER_EMAILS_MAX_LENGTH}
                />
                <PrivilegedTextField
                    sufficientPrivileges={sufficientPrivileges}
                    label={t('members.phone')}
                    state={phoneState}
                    validator={validators.phone}
                    className="member-detail-tab--fields--phone"
                    inputMode="tel"
                    placeholder={t('members.placeholder.phone')}
                    maxLength={MEMBER_PHONE_MAX_LENGTH}
                />
                {memberTypeState.value.is(MEMBER) && clearingEnabled && (
                    <PrivilegedSelectField
                        sufficientPrivileges={sufficientPrivileges}
                        label={t('members.payment-currency')}
                        state={paymentCurrencyCodeState}
                        className="member-detail-tab--fields--payment-currency"
                        inputId="member-detail-tab--fields--payment-currency"
                        options={availablePaymentCurrencies.map(({ code: availablePaymentCurrencyCode }) => (
                            <option value={availablePaymentCurrencyCode} key={availablePaymentCurrencyCode}>{availablePaymentCurrencyCode}</option>
                        ))}
                        isRequired
                    />
                )}
                {(memberTypeState.value.is(TECHNICAL_AUCTIONEER) || memberTypeState.value.is(AUCTIONEER_AND_TECH_AUCTIONEER)) && (
                    <>
                        {clearingEnabled && (
                            <PrivilegedCheckboxField
                                sufficientPrivileges={sufficientPrivileges}
                                label={t('members.foreign-currency-allowed')}
                                state={foreignCurrencyAllowedState}
                                className="member-detail-tab--fields--foreign-currency-allowed"
                                inputId="member-detail-tab--fields--foreign-currency-allowed"
                            />
                        )}
                        {euAuctioneersEnabled && (
                            <PrivilegedCheckboxField
                                sufficientPrivileges={sufficientPrivileges}
                                label={t('members.eu-auctioneer')}
                                state={euAuctioneerState}
                                className="member-detail-tab--fields--eu-auctioneer"
                                inputId="member-detail-tab--fields--eu-auctioneer"
                            />
                        )}
                    </>
                )}
            </div>
            {availableZones.length > 1 && (
                <div className="member-detail-tab--zones">
                    <PrivilegedCheckboxList
                        sufficientPrivileges={sufficientPrivileges}
                        label={t('members.zones')}
                        name="zones-list"
                        values={availableZonesMap}
                        state={zonesState}
                        className="member-detail-tab--fields--zones"
                    />
                </div>
            )}
            {memberTypeState.value.is(MEMBER) && clearingEnabled && <MemberCashLimitsTable cashLimits={member.cashLimits} />}
            {member.users.length > 0 && <MemberAssignedUsersTable users={member.users} />}
            <Privileged sufficientPrivileges={sufficientPrivileges}>
                <div className="rich-list-row-expanded--buttons">
                    <div className="rich-list-row-expanded--buttons--left">
                        <ApplyChangesButton
                            extendedMutation={updateMemberMutation}
                            disabled={isDisabled}
                            confirmationMessage={t('members.confirm-update-member', { name: member.name, memberId: member.memberId })}
                            extraConfirmationMessage={t('members.extra-confirm-update-member', { name: member.name, memberId: member.memberId })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--middle">
                        <SuspensionButton
                            isSuspended={suspendedState.value}
                            extendedMutation={toggleSuspendMemberMutation}
                            confirmationMessage={t(toggleSuspendMemberConfirmMessageKey, { name: member.name, memberId: member.memberId })}
                            extraConfirmationMessage={t('members.extra-confirm-suspend-member', { name: member.name, memberId: member.memberId })}
                        />
                        <ActivationButton
                            isActive={activeState.value}
                            disabled={isActivationButtonDisabled}
                            extendedMutation={toggleMemberActivationMutation}
                            confirmationMessage={t(toggleMemberActivationConfirmMessageKey, { name: member.name, memberId: member.memberId })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--right" />
                </div>
                {member.deactivationBlockers?.map((deactivationBlocker) => (
                    <InfoPanel
                        key={deactivationBlocker}
                        message={transformMemberBlockers(t, deactivationBlocker)}
                    />
                ))}
                {member.modificationBlockers?.map((modificationBlocker) => (
                    <InfoPanel
                        key={modificationBlocker}
                        message={transformMemberBlockers(t, modificationBlocker)}
                    />
                ))}
            </Privileged>
        </ExpandedListTab>
    );
}

export default MemberDetailTab;
